/**
 * <savette-selector> — the shared horizontal selector used by filter/sort controls,
 * colour swatch rows (cards and product page), visual category tiles and the size-guide style row.
 *
 * Markup contract (rendered by snippets/selector-item.liquid):
 *   <savette-selector mode="single|multi" deselectable data-group name="color" class="selector selector--swatch">
 *     <button type="button" class="selector__item" data-value="Black" aria-pressed="true">…</button>
 *     <a class="selector__item" href="…" aria-current="page">…</a>   (navigation items: no toggling)
 *     <button type="button" class="selector__item selector__item--more" data-more>+3</button>
 *   </savette-selector>
 *
 * Behaviour:
 *   - single mode: one item active (aria-pressed="true"); re-tapping the active item clears it when `deselectable`.
 *   - role: group in multi mode or with `data-group` (rows of toggle buttons), radiogroup otherwise.
 *   - multi mode: each item toggles independently.
 *   - arrow keys move focus along the row (roving tabindex); Home/End jump; Space/Enter toggles.
 *   - items past `data-limit` are hidden until the "+N" item is activated (or always shown when no limit).
 *   - a "+N" rendered as a link (product cards) navigates to its href instead of revealing the rest; it still
 *     reads "+N" and shows only when items are hidden.
 *   - dispatches `selector:change` ({ name, value, values, item }) on the element after any change.
 *   - if `name` is set and the element sits inside a form, hidden inputs mirror the selection so a plain
 *     form submit (filters) works without JavaScript on the consumer side.
 */
class SavetteSelector extends HTMLElement {
  static get observedAttributes() {
    return ['mode', 'deselectable'];
  }

  connectedCallback() {
    // a row of toggle buttons that marks itself data-group (product card swatches) is a group, not
    // a radio group; other single-choice rows keep radiogroup
    this.setAttribute('role', this.mode === 'multi' || this.hasAttribute('data-group') ? 'group' : 'radiogroup');
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('change', this.onChange);
    this.applyLimit();
    this.syncTabindex();
    this.syncInputs();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('change', this.onChange);
  }

  /** Items that wrap a real form input (filters, sort) rather than a button. */
  get inputBacked() {
    return this.querySelector('.selector__input') !== null;
  }

  get mode() {
    return this.getAttribute('mode') === 'multi' ? 'multi' : 'single';
  }

  get deselectable() {
    return this.hasAttribute('deselectable');
  }

  get items() {
    return [...this.querySelectorAll('.selector__item:not([data-more])')];
  }

  get values() {
    if (this.inputBacked) {
      return this.items.filter((i) => i.querySelector('.selector__input')?.checked).map((i) => i.dataset.value);
    }
    return this.items.filter((i) => i.getAttribute('aria-pressed') === 'true').map((i) => i.dataset.value);
  }

  get value() {
    return this.values[0] ?? null;
  }

  onClick = (event) => {
    const more = event.target.closest('[data-more]');
    if (more && this.contains(more)) {
      if (more.matches('a')) return; // the link form navigates to the product page
      event.preventDefault();
      this.revealAll();
      return;
    }
    const item = event.target.closest('.selector__item');
    if (!item || !this.contains(item) || item.matches('a')) return; // links navigate; no toggle state
    if (item.querySelector('.selector__input')) return; // the input's own change event drives these
    event.preventDefault();
    this.toggle(item);
  };

  /** Input-backed items: the browser has already toggled the input; report the new selection. */
  onChange = (event) => {
    const item = event.target.closest('.selector__item');
    if (!item || !this.contains(item)) return;
    this.syncTabindex(item);
    this.dispatchEvent(
      new CustomEvent('selector:change', {
        bubbles: true,
        detail: { name: this.getAttribute('name'), value: this.value, values: this.values, item },
      }),
    );
  };

  onKeydown = (event) => {
    const item = event.target.closest('.selector__item');
    if (!item) return;
    const visible = this.items.filter((i) => !i.hidden);
    const index = visible.indexOf(item);
    let next = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = visible[(index + 1) % visible.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = visible[(index - 1 + visible.length) % visible.length];
        break;
      case 'Home':
        next = visible[0];
        break;
      case 'End':
        next = visible[visible.length - 1];
        break;
      case ' ':
      case 'Enter':
        if (item.matches('button') && !item.hasAttribute('data-more')) {
          event.preventDefault();
          this.toggle(item);
        }
        return;
      default:
        return;
    }
    event.preventDefault();
    next?.focus();
    this.syncTabindex(next);
  };

  toggle(item) {
    const wasActive = item.getAttribute('aria-pressed') === 'true';
    if (this.mode === 'single') {
      if (wasActive && !this.deselectable) return;
      this.items.forEach((i) => i.setAttribute('aria-pressed', 'false'));
      if (!wasActive) item.setAttribute('aria-pressed', 'true');
    } else {
      item.setAttribute('aria-pressed', wasActive ? 'false' : 'true');
    }
    this.syncInputs();
    this.syncTabindex(item);
    this.dispatchEvent(
      new CustomEvent('selector:change', {
        bubbles: true,
        detail: { name: this.getAttribute('name'), value: this.value, values: this.values, item },
      }),
    );
  }

  /** Programmatically set the active value(s) without dispatching. */
  select(values) {
    const set = new Set([].concat(values ?? []));
    this.items.forEach((i) => i.setAttribute('aria-pressed', set.has(i.dataset.value) ? 'true' : 'false'));
    this.syncInputs();
  }

  applyLimit() {
    const limit = Number(this.dataset.limit);
    const more = this.querySelector('[data-more]');
    if (!limit || !more) return;
    const items = this.items;
    items.forEach((i, idx) => {
      // never hide the active item (a pressed button, or the current link on the product page's
      // colourway row); collapse the row around it
      i.hidden = idx >= limit && !this.isActive(i);
    });
    // "+N" counts what is actually hidden, so an active item kept past the limit is not counted
    const hiddenCount = items.filter((i) => i.hidden).length;
    more.hidden = hiddenCount === 0;
    more.textContent = `+${hiddenCount}`;
  }

  /**
   * The active item: a pressed toggle, or a navigation link marked current — the current page, or
   * (aria-current="true") the filter applied to it, as the category row's filter tiles are.
   */
  isActive(item) {
    const current = item.getAttribute('aria-current');
    return item.getAttribute('aria-pressed') === 'true' || current === 'page' || current === 'true';
  }

  revealAll() {
    this.items.forEach((i) => (i.hidden = false));
    const more = this.querySelector('[data-more]');
    if (more) more.hidden = true;
    this.syncTabindex();
    this.items.find((i) => i.hidden === false && i.tabIndex === 0)?.focus();
  }

  syncTabindex(focused) {
    // Rows of real inputs (filters, sort) are already in the tab order through the inputs; a
    // tabindex on the wrapping label would add a second stop per option (SAVETTE-61).
    if (this.inputBacked) return;
    const visible = this.items.filter((i) => !i.hidden);
    const current = focused ?? visible.find((i) => this.isActive(i)) ?? visible[0];
    visible.forEach((i) => (i.tabIndex = i === current ? 0 : -1));
  }

  syncInputs() {
    const name = this.getAttribute('name');
    if (!name || this.inputBacked) return; // input-backed rows carry their own fields
    this.querySelectorAll('input[type="hidden"][data-selector-input]').forEach((i) => i.remove());
    this.values.forEach((v) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = v;
      input.dataset.selectorInput = '';
      this.appendChild(input);
    });
  }
}

if (!customElements.get('savette-selector')) customElements.define('savette-selector', SavetteSelector);
